// app/(app)/employees/[id].tsx
import { Button, Input } from "@/components/default/forms";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  deleteEmployee,
  fetchEmployeeById,
  fetchEmployeePermissions,
  resetPermissions,
  resetSelected,
  updateEmployee,
  updateEmployeePermissions,
} from "@/store/slices/employeeSlice";
import { showAlert } from "@/utils/alert";
import { Ionicons } from "@expo/vector-icons";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

export default function EditEmployeeScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const {
    selectedEmployee,
    loading,
    updating,
    error,
    permissions,
    permissionsLoading,
    permissionsError,
    permissionsUpdating,
  } = useAppSelector((state) => state.employees);
  const { user } = useAppSelector((state) => state.auth);
  const isOwner = user?.userType === "Owner";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [designation, setDesignation] = useState("");
  const [phone, setPhone] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    designation: "",
    phone: "",
  });

  // Load employee data
  useEffect(() => {
    dispatch(fetchEmployeeById(Number(id)));
    return () => {
      dispatch(resetSelected());
      dispatch(resetPermissions());
    };
  }, [id]);

  // Populate form with loaded data
  useEffect(() => {
    if (selectedEmployee) {
      setName(selectedEmployee.ownerName);
      setEmail(selectedEmployee.email);
      setDesignation(selectedEmployee.designation);
      setPhone(selectedEmployee.phone);
      setIsActive(selectedEmployee.isVerified === true);
      // If owner, fetch permissions when employee is loaded
      if (isOwner) {
        dispatch(fetchEmployeePermissions(selectedEmployee.id));
      }
    }
  }, [selectedEmployee, isOwner]);

  // Show API errors
  useEffect(() => {
    if (error) showAlert("Error", error);
  }, [error]);

  useEffect(() => {
    if (permissionsError) showAlert("Error", permissionsError);
  }, [permissionsError]);

  const validate = () => {
    let valid = true;
    const newErrors = { name: "", email: "", designation: "", phone: "" };
    if (!name.trim()) {
      newErrors.name = "Name is required";
      valid = false;
    }
    if (!email.trim()) {
      newErrors.email = "Email is required";
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Invalid email";
      valid = false;
    }
    if (!designation.trim()) {
      newErrors.designation = "Designation is required";
      valid = false;
    }
    if (!phone.trim()) {
      newErrors.phone = "Phone is required";
      valid = false;
    } else if (!/^\d{10}$/.test(phone)) {
      newErrors.phone = "10-digit number required";
      valid = false;
    }
    setErrors(newErrors);
    return valid;
  };

  const validatePassword = () => {
    if (!newPassword && !confirmPassword) return true;
    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const handleUpdate = async () => {
    if (!validate()) return;
    if (!validatePassword()) return;
    const updateData: any = {
      ownerName: name,
      email,
      designation,
      phone,
      isActive: isActive ? 1 : 0,
    };
    if (newPassword && newPassword === confirmPassword) {
      updateData.password = newPassword;
    }
    const result = await dispatch(
      updateEmployee({ id: Number(id), data: updateData }),
    );
    if (updateEmployee.fulfilled.match(result)) {
      showAlert("Success", "Employee updated", [
        { text: "OK", onPress: () => router.back() },
      ]);
    }
  };

  const handleDelete = () => {
    showAlert("Delete Employee", `Are you sure you want to delete ${name}?`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Delete",
        style: "destructive",
        onPress: async () => {
          const result = await dispatch(deleteEmployee(Number(id)));
          if (deleteEmployee.fulfilled.match(result)) {
            showAlert("Deleted", "Employee removed", [
              { text: "OK", onPress: () => router.back() },
            ]);
          }
        },
      },
    ]);
  };

  const handleTogglePermission = (
    permissionId: number,
    currentGranted: boolean,
  ) => {
    // Update local permission state (we'll send all permissions on save)
    const updatedPermissions = permissions.map((p) =>
      p.id === permissionId ? { ...p, granted: !currentGranted } : p,
    );
    // Update state via slice? We'll just update local state and use a save button.
    // We can also update the slice directly but we need to manage it.
    // Since we have permissions in slice, we can dispatch an action to update local copy.
    // For simplicity, we'll use local state for toggling before saving.
    // But we need to persist toggles. We'll create a local state for permissions.
  };

  // We'll manage permissions locally to avoid complex slice updates.
  const [localPermissions, setLocalPermissions] = useState<typeof permissions>(
    [],
  );

  // Sync local permissions when slice permissions load
  useEffect(() => {
    if (permissions.length > 0 && localPermissions.length === 0) {
      setLocalPermissions(permissions);
    }
  }, [permissions]);

  const handleToggle = (permissionId: number) => {
    setLocalPermissions((prev) =>
      prev.map((p) =>
        p.id === permissionId ? { ...p, granted: !p.granted } : p,
      ),
    );
  };

  const handleSavePermissions = async () => {
    const payload = localPermissions.map((p) => ({
      permissionId: p.id,
      granted: p.granted,
    }));
    const result = await dispatch(
      updateEmployeePermissions({
        employeeId: Number(id),
        permissions: payload,
      }),
    );
    if (updateEmployeePermissions.fulfilled.match(result)) {
      showAlert("Success", "Permissions updated successfully");
      // Refresh permissions to get latest
      dispatch(fetchEmployeePermissions(Number(id)));
    }
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    keyboardView: {
      flex: 1,
    },
    scrollContent: {
      padding: spacing.lg,
      paddingBottom: spacing.xl,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
    },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.xs,
    },
    idText: {
      fontSize: 14,
      color: colors.textLight,
      marginBottom: spacing.lg,
    },
    inputContainer: {
      marginBottom: spacing.lg,
    },
    statusContainer: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingVertical: spacing.sm,
      marginBottom: spacing.lg,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    statusLabel: {
      fontSize: 16,
      fontWeight: "500",
      color: colors.text,
    },
    statusBadge: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.sm,
    },
    statusText: {
      fontSize: 14,
      fontWeight: "500",
      color: isActive ? "#22c55e" : "#ef4444",
    },
    passwordHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.md,
      marginTop: spacing.sm,
    },
    passwordSectionTitle: {
      fontSize: 18,
      fontWeight: "600",
      color: colors.text,
    },
    permissionsSection: {
      marginTop: spacing.xl,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: spacing.lg,
    },
    permissionsTitle: {
      fontSize: 18,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.md,
    },
    permissionItem: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingVertical: spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    permissionName: {
      fontSize: 14,
      color: colors.text,
    },
    permissionCode: {
      fontSize: 12,
      color: colors.textLight,
    },
    savePermissionsButton: {
      marginTop: spacing.md,
    },
    deleteButton: {
      marginTop: spacing.xl,
    },
  });

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={styles.title}>Edit Employee</Text>
          <Text style={styles.idText}>
            ID: {selectedEmployee?.employeeCode || id}
          </Text>

          {/* Form fields ... */}
          <Input
            label="Full Name"
            value={name}
            onChangeText={setName}
            error={errors.name}
            containerStyle={styles.inputContainer}
          />
          <Input
            label="Email"
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            error={errors.email}
            containerStyle={styles.inputContainer}
          />
          <Input
            label="Designation"
            value={designation}
            onChangeText={setDesignation}
            error={errors.designation}
            containerStyle={styles.inputContainer}
          />
          <Input
            label="Phone Number"
            value={phone}
            onChangeText={setPhone}
            keyboardType="phone-pad"
            error={errors.phone}
            containerStyle={styles.inputContainer}
          />

          <View style={styles.statusContainer}>
            <Text style={styles.statusLabel}>Status</Text>
            <View style={styles.statusBadge}>
              <Text style={styles.statusText}>
                {isActive ? "Active" : "Inactive"}
              </Text>
              <Switch
                value={isActive}
                onValueChange={setIsActive}
                trackColor={{ false: colors.border, true: colors.primary }}
                thumbColor={colors.background}
              />
            </View>
          </View>

          {/* Password Change */}
          <TouchableOpacity
            style={styles.passwordHeader}
            onPress={() => setShowPasswordSection(!showPasswordSection)}
          >
            <Text style={styles.passwordSectionTitle}>Change Password</Text>
            <Ionicons
              name={showPasswordSection ? "chevron-up" : "chevron-down"}
              size={24}
              color={colors.textLight}
            />
          </TouchableOpacity>

          {showPasswordSection && (
            <>
              <Input
                label="New Password"
                value={newPassword}
                onChangeText={setNewPassword}
                secureTextEntry
                placeholder="At least 6 characters"
                error={passwordError && !newPassword ? passwordError : ""}
                containerStyle={styles.inputContainer}
              />
              <Input
                label="Confirm Password"
                value={confirmPassword}
                onChangeText={setConfirmPassword}
                secureTextEntry
                placeholder="Retype new password"
                error={
                  passwordError && newPassword !== confirmPassword
                    ? passwordError
                    : ""
                }
                containerStyle={styles.inputContainer}
              />
            </>
          )}

          {/* Permissions Section (Owner only) */}
          {isOwner && (
            <View style={styles.permissionsSection}>
              <Text style={styles.permissionsTitle}>Permissions</Text>
              {permissionsLoading ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : permissionsError ? (
                <Text style={{ color: colors.danger }}>{permissionsError}</Text>
              ) : localPermissions.length === 0 ? (
                <Text style={{ color: colors.textLight }}>
                  No permissions available
                </Text>
              ) : (
                localPermissions.map((p) => (
                  <View key={p.id} style={styles.permissionItem}>
                    <View>
                      <Text style={styles.permissionName}>{p.name}</Text>
                      <Text style={styles.permissionCode}>{p.code}</Text>
                    </View>
                    <Switch
                      value={p.granted}
                      onValueChange={() => handleToggle(p.id)}
                      trackColor={{
                        false: colors.border,
                        true: colors.primary,
                      }}
                      thumbColor={colors.background}
                    />
                  </View>
                ))
              )}
              {!permissionsLoading && localPermissions.length > 0 && (
                <Button
                  title="Save Permissions"
                  onPress={handleSavePermissions}
                  loading={permissionsUpdating}
                  containerStyle={styles.savePermissionsButton}
                />
              )}
            </View>
          )}

          {/* Update & Delete buttons */}
          <Button
            title="Update Employee"
            onPress={handleUpdate}
            loading={updating}
          />
          <Button
            title="Delete Employee"
            onPress={handleDelete}
            variant="danger"
            containerStyle={styles.deleteButton}
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
